import { t } from 'i18next'

import { AppBreakpoint, PageNature } from '@makyoapp/makyo-sdk/models/AppBuilder'
import { BuilderDroppableElement, ElementSettings } from '@makyoapp/makyo-common'
import {
  ArrowShortDown16,
  ArrowShortLeft16,
  ArrowShortRight16,
  ArrowShortUp16,
  RadiusAll16,
} from '@makyoapp/makyo-icon/icon'
import { CornerRadius } from '@makyoapp/makyo-tw-ui'

type WithBreakPoints<T> = Record<AppBreakpoint, T>

export type OverlayPlacement = 'top' | 'bottom' | 'right' | 'left' | 'center'

export interface BuilderOverlay {
  id: string
  title: string
  type: string
  slug?: string
  icon: string
  actions: any
  enabled: boolean
  tableId?: string
  rowId?: string
  rowIndex?: number
  visible: boolean
  visibility: {
    isAny: boolean
    conditions: any[]
  }
  elementSettings: ElementSettings
  elements: BuilderDroppableElement[]
  closeBehavior: 'direct' | 'back'
  bgColor: string
  opacity: number

  // breakpoints related settings
  placement: WithBreakPoints<OverlayPlacement>
  size: WithBreakPoints<string>
  shape: WithBreakPoints<string>
  nature?: PageNature
  alignment: 'start' | 'center' | 'end'
}

export const DEFAULT_OVERLAY_PLACEMENT: WithBreakPoints<OverlayPlacement> = {
  phone: 'bottom',
  'phone-landscape': 'left',
  tablet: 'bottom',
  'tablet-landscape': 'left',
  desktop: 'left',
  tv: 'left',
}

export const DEFAULT_OVERLAY_SIZE: WithBreakPoints<string> = {
  phone: '90%, 100%',
  'phone-landscape': '50%, 100%',
  tablet: '90%, 100%',
  'tablet-landscape': '50%, 100%',
  desktop: '40%, 100%',
  tv: '40%, 100%',
}

export const DEFAULT_OVERLAY_SHAPE: WithBreakPoints<CornerRadius> = {
  phone: 'medium',
  'phone-landscape': 'medium',
  tablet: 'medium',
  'tablet-landscape': 'medium',
  desktop: 'medium',
  tv: 'medium',
}

export function switchDirectionPlacement(direction: string | OverlayPlacement): OverlayPlacement {
  return direction as OverlayPlacement
}

export function getDirectionLabel(direction: string | OverlayPlacement): string {
  switch (direction) {
    case 'top':
      return t('Top to Bottom')
    case 'bottom':
      return t('Bottom to Top')
    case 'right':
      return t('Right to Left')
    case 'left':
      return t('Left to Right')
    case 'center':
      return t('Center')
    default:
      return t('Bottom to Top')
  }
}

export function getOverlaySizeLabel(direction: string | OverlayPlacement): string {
  if (direction === 'center') {
    return t('Width')
  }
  if (direction === 'left' || direction === 'right') {
    return t('Width')
  }

  if (direction === 'top' || direction === 'bottom') {
    return t('Height')
  }
  return t('Height')
}

export function getDrawerSize(value?: string) {
  // If value is undefined, set default to '50%'
  const [_v1, _v2] = value?.split(',').map((s) => s.trim()) || ['50%', '50%']
  const size = {
    v1: _v1 ? parseInt(_v1.replace('%', ''), 10) || 50 : 50,
    v2: _v2 ? parseInt(_v2.replace('%', ''), 10) || 50 : 50,
  }
  return size
}

export function formatDrawerSize(type: 'side' | 'center', currentSize: string, newSize: string) {
  const [v1, v2] = currentSize?.split(',').map((s) => s.trim()) || []

  // for `side` update, the format is: [height, width]
  if (type === 'side') {
    return `${newSize},${v2}`
  }

  // for `center` update, the format is: [width, height]
  return `${v1},${newSize}`
}

/**
 * @note the placement here is A REVERSE of direction
 */
export const overlayPlacements = [
  { value: 'right', icon: <ArrowShortLeft16 width={16} height={16} /> },
  { value: 'left', icon: <ArrowShortRight16 width={16} height={16} /> },
  { value: 'center', icon: <RadiusAll16 width={16} height={16} /> },
  { value: 'bottom', icon: <ArrowShortUp16 width={16} height={16} /> },
  { value: 'top', icon: <ArrowShortDown16 width={16} height={16} /> },
]

export const overlayShapeOptions = [
  { value: 'none', label: 'None' },
  { value: 'extra-small', label: 'Extra Small' },
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
  { value: 'extra-large', label: 'Extra Large' },
  { value: 'full', label: 'Full' },
]

export const DEFAULT_OVERLAY_SETTINGS = {
  placement: DEFAULT_OVERLAY_PLACEMENT,
  size: DEFAULT_OVERLAY_SIZE,
  shape: DEFAULT_OVERLAY_SHAPE,
  closeBehavior: 'direct',
  opacity: 85,
}
